import {
  updateMemberProfile,
  type EducationStatus,
  type Evenement,
  type EvenementInscription,
  type GalleryItem,
  type Inscription,
  type InscriptionStatus,
  type Member,
  type MemberProfileSnapshot,
} from '@mtc/shared';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GalleryLightbox } from '../components/GalleryLightbox';
import { trackEvent } from '../lib/track';
import { StateMessage } from '../components/StateMessage';
import {
  useEvenements,
  useFormations,
  useGalleryForMember,
  useMyEvenementInscriptions,
  useMyInscriptions,
  useUpdateMyEvenementInscription,
} from '../data/queries';
import { useSEO } from '../hooks/useSEO';
import { useAuth } from '../lib/auth';

const STATUS_LABEL: Record<InscriptionStatus, string> = {
  pending_contact: 'En attente de contact',
  pending_confirmation: 'En attente de confirmation',
  confirmed: 'Confirmée',
  enrolled: 'Inscrit·e (payé)',
  cancelled: 'Annulée',
};

const STATUS_COLOR: Record<InscriptionStatus, { bg: string; fg: string }> = {
  pending_contact: { bg: 'rgba(217,119,6,0.12)', fg: '#A36408' },
  pending_confirmation: { bg: 'rgba(37,99,235,0.12)', fg: '#1E4ED8' },
  confirmed: { bg: 'rgba(11,93,59,0.12)', fg: '#0B5D3B' },
  enrolled: { bg: 'rgba(11,93,59,0.18)', fg: '#0B5D3B' },
  cancelled: { bg: 'rgba(200,16,46,0.12)', fg: '#9B1C2C' },
};

export function MonEspace() {
  const navigate = useNavigate();
  const {
    user,
    member,
    isLoading,
    isVerified,
    sendVerification,
    refresh,
    signOut,
    lastLinkedCount,
  } = useAuth();
  const inscriptionsQ = useMyInscriptions(user?.uid);
  const evenementInscriptionsQ = useMyEvenementInscriptions(user?.uid);
  const evenementsQ = useEvenements();
  const formationsQ = useFormations();
  const galleryQ = useGalleryForMember(user?.uid);
  const updateEvenementInscriptionMutation = useUpdateMyEvenementInscription();

  // Lightbox state — shared across all inscription cards. The card that
  // opened the lightbox passes its filtered photo list when triggering.
  const [activeGallery, setActiveGallery] = useState<{
    items: GalleryItem[];
    index: number;
  } | null>(null);

  useSEO({
    title: 'Mon espace — MTC',
    description: 'Tableau de bord membre MTC.',
  });

  if (isLoading) return <StateMessage variant="loading" />;
  if (!user) {
    navigate('/connexion', { replace: true, state: { from: '/mon-espace' } });
    return null;
  }

  const inscriptions = inscriptionsQ.data ?? [];
  const evenementInscriptions = evenementInscriptionsQ.data ?? [];
  const evenements = evenementsQ.data ?? [];
  const evenementById = (id: string) => evenements.find((e) => e.id === id);
  const formationTitleById = (id: string) =>
    formationsQ.data?.find((f) => f.id === id)?.titre ?? id;

  const galleryItems = galleryQ.data ?? [];

  return (
    <div style={{ maxWidth: 960, margin: '64px auto 96px', padding: '0 24px' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 32,
        }}
      >
        <div>
          <span className="mtc-eyebrow">Espace membre</span>
          <h1
            style={{
              fontFamily: 'var(--mtc-font-display)',
              fontSize: 'clamp(32px, 4vw, 52px)',
              fontWeight: 400,
              lineHeight: 1.05,
              letterSpacing: '-0.03em',
              margin: '8px 0 4px',
            }}
          >
            Bonjour {user.displayName?.split(' ')[0] ?? 'cher·e praticien·ne'}
          </h1>
          <p style={{ fontSize: 14, color: 'rgba(15,25,22,0.6)', margin: 0 }}>{user.email}</p>
        </div>
        <button type="button" className="mtc-btn mtc-btn-ghost" onClick={() => signOut()}>
          Se déconnecter
        </button>
      </header>

      {!isVerified && (
        <VerifyBanner onResend={sendVerification} onRefresh={refresh} />
      )}

      {isVerified && lastLinkedCount !== null && lastLinkedCount > 0 && (
        <div
          style={{
            marginBottom: 24,
            padding: '12px 16px',
            background: 'rgba(11,93,59,0.08)',
            border: '1px solid rgba(11,93,59,0.18)',
            borderRadius: 10,
            fontSize: 13,
            color: '#0B5D3B',
          }}
        >
          ✓ {lastLinkedCount} inscription{lastLinkedCount > 1 ? 's' : ''} attachée
          {lastLinkedCount > 1 ? 's' : ''} à votre compte.
        </div>
      )}

      {/* Mes informations — read by default, editable when admin clicks
          "Modifier". The values feed the inscription form's pre-fill so the
          member doesn't have to retype them for each new formation. */}
      <MesInformations user={user} member={member} onSaved={refresh} />

      <section>
        <h2
          style={{
            fontFamily: 'var(--mtc-font-mono)',
            fontSize: 11,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'rgba(15,25,22,0.55)',
            marginBottom: 16,
          }}
        >
          Découvrir plus
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 14,
          }}
        >
          <DiscoverTile
            tone="vert"
            eyebrow="Réservé aux membres"
            title="Publications"
            desc="Articles et actualités scientifiques de l'équipe MTC."
            onClick={() => {
              trackEvent('publications_open');
              navigate('/publications');
            }}
          />
          <DiscoverTile
            tone="or"
            eyebrow="Catalogue"
            title="Nos formations"
            desc="Découvrez toutes les masterclass et leurs prochaines éditions."
            onClick={() => navigate('/formations')}
          />
          <DiscoverTile
            tone="rouge"
            eyebrow="Agenda"
            title="Événements scientifiques"
            desc="Congrès, symposiums et rencontres de la communauté."
            onClick={() => navigate('/evenements')}
          />
        </div>
      </section>

      <section style={{ marginTop: 48 }}>
        <h2
          style={{
            fontFamily: 'var(--mtc-font-mono)',
            fontSize: 11,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'rgba(15,25,22,0.55)',
            marginBottom: 16,
          }}
        >
          Mes inscriptions
          {inscriptions.length > 0 ? ` · ${inscriptions.length}` : ''}
        </h2>

        {inscriptionsQ.isLoading ? (
          <StateMessage variant="loading" />
        ) : inscriptions.length === 0 ? (
          <EmptyState onBrowse={() => navigate('/formations')} verified={isVerified} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {inscriptions.map((i) => (
              <InscriptionCard
                key={i.id}
                inscription={i}
                formationTitle={formationTitleById(i.formationId)}
                gallery={galleryItems}
                onOpenLightbox={(items, index) => {
                  trackEvent('gallery_open');
                  setActiveGallery({ items, index });
                }}
              />
            ))}
          </div>
        )}
      </section>

      {(evenementInscriptions.length > 0 || evenementInscriptionsQ.isLoading) && (
        <section style={{ marginTop: 48 }}>
          <h2
            style={{
              fontFamily: 'var(--mtc-font-mono)',
              fontSize: 11,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'rgba(15,25,22,0.55)',
              marginBottom: 16,
            }}
          >
            Mes événements
            {evenementInscriptions.length > 0 ? ` · ${evenementInscriptions.length}` : ''}
          </h2>
          {evenementInscriptionsQ.isLoading ? (
            <StateMessage variant="loading" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {evenementInscriptions.map((ins) => (
                <EvenementInscriptionCard
                  key={ins.id}
                  inscription={ins}
                  evenement={evenementById(ins.evenementId) ?? null}
                  onCancelInscription={async () => {
                    if (!window.confirm('Annuler votre inscription complète à cet événement ?'))
                      return;
                    try {
                      await updateEvenementInscriptionMutation.mutateAsync({
                        id: ins.id,
                        patch: { status: 'cancelled' },
                      });
                      await evenementInscriptionsQ.refetch();
                    } catch (err) {
                      alert(
                        `Annulation impossible : ${err instanceof Error ? err.message : String(err)}`,
                      );
                    }
                  }}
                  onRemoveAtelier={async (atelierId, newTotal) => {
                    if (!window.confirm('Retirer cet atelier de votre inscription ?')) return;
                    try {
                      const nextIds = ins.atelierIds.filter((id) => id !== atelierId);
                      await updateEvenementInscriptionMutation.mutateAsync({
                        id: ins.id,
                        patch: { atelierIds: nextIds, totalAmount: newTotal },
                      });
                      await evenementInscriptionsQ.refetch();
                    } catch (err) {
                      alert(
                        `Retrait impossible : ${err instanceof Error ? err.message : String(err)}`,
                      );
                    }
                  }}
                  onRemoveMasterclass={async (masterclassId, newTotal) => {
                    if (!window.confirm('Retirer cette masterclass de votre inscription ?')) return;
                    try {
                      const nextIds = (ins.masterclassIds ?? []).filter(
                        (id) => id !== masterclassId,
                      );
                      await updateEvenementInscriptionMutation.mutateAsync({
                        id: ins.id,
                        patch: { masterclassIds: nextIds, totalAmount: newTotal },
                      });
                      await evenementInscriptionsQ.refetch();
                    } catch (err) {
                      alert(
                        `Retrait impossible : ${err instanceof Error ? err.message : String(err)}`,
                      );
                    }
                  }}
                />
              ))}
            </div>
          )}
        </section>
      )}

      <GalleryLightbox
        items={activeGallery?.items ?? []}
        activeIndex={activeGallery?.index ?? null}
        onChange={(next) => {
          if (next === null) setActiveGallery(null);
          else if (activeGallery) setActiveGallery({ ...activeGallery, index: next });
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// EvenementInscriptionCard — card for one event inscription, with partial
// atelier cancellation (one × button per atelier) and full inscription cancel.
// ─────────────────────────────────────────────────────────────────────────

const EVENT_STATUS_LABEL: Record<EvenementInscription['status'], string> = {
  pending: 'En attente',
  confirmed: 'Confirmée',
  paid: 'Payée',
  cancelled: 'Annulée',
};
const EVENT_STATUS_COLOR: Record<EvenementInscription['status'], { bg: string; fg: string }> = {
  pending: { bg: 'rgba(217,119,6,0.12)', fg: '#A36408' },
  confirmed: { bg: 'rgba(37,99,235,0.12)', fg: '#1E4ED8' },
  paid: { bg: 'rgba(11,93,59,0.18)', fg: '#0B5D3B' },
  cancelled: { bg: 'rgba(200,16,46,0.12)', fg: '#9B1C2C' },
};
const EVENT_PAYMENT_PREF_LABEL: Record<string, string> = {
  virement: 'Virement',
  cheque: 'Chèque',
  espece: 'Espèces',
  'installments-2': '2 échéances',
  'installments-3': '3 échéances',
  'sur-place': 'Sur place le jour J',
};

function EvenementInscriptionCard({
  inscription,
  evenement,
  onCancelInscription,
  onRemoveAtelier,
  onRemoveMasterclass,
}: {
  inscription: EvenementInscription;
  evenement: Evenement | null;
  onCancelInscription: () => void;
  onRemoveAtelier: (atelierId: string, newTotal: number) => void;
  onRemoveMasterclass: (masterclassId: string, newTotal: number) => void;
}) {
  const ateliers = evenement?.hostedRegistration?.ateliers ?? [];
  const masterclasses = evenement?.hostedRegistration?.masterclasses ?? [];
  const packs = evenement?.hostedRegistration?.packs ?? [];
  const generalPrice = evenement?.hostedRegistration?.generalInscription?.price ?? 0;
  const statusColor = EVENT_STATUS_COLOR[inscription.status];
  const isCancellable = inscription.status !== 'cancelled' && inscription.status !== 'paid';
  const selectedAteliers = inscription.atelierIds
    .map((aid) => ateliers.find((a) => a.id === aid))
    .filter((a): a is NonNullable<typeof a> => Boolean(a));
  const selectedMasterclasses = (inscription.masterclassIds ?? [])
    .map((mid) => masterclasses.find((m) => m.id === mid))
    .filter((m): m is NonNullable<typeof m> => Boolean(m));
  const selectedPack = inscription.packId
    ? packs.find((p) => p.id === inscription.packId) ?? null
    : null;

  return (
    <article
      style={{
        padding: '20px 22px',
        background: 'white',
        border: '1px solid rgba(15,25,22,0.1)',
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div
            style={{
              fontSize: 10,
              fontFamily: 'var(--mtc-font-mono)',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: 'rgba(15,25,22,0.5)',
              marginBottom: 4,
            }}
          >
            Événement{evenement?.partnerName ? ` · ${evenement.partnerName}` : ''}
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 600, color: '#0F1916', margin: 0 }}>
            {evenement?.title ?? inscription.evenementId}
          </h3>
          {evenement?.startDate && (
            <div style={{ fontSize: 13, color: 'rgba(15,25,22,0.65)', marginTop: 4 }}>
              {new Date(evenement.startDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              {evenement.city ? ` · ${evenement.city}` : ''}
            </div>
          )}
        </div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            padding: '4px 10px',
            borderRadius: 999,
            background: statusColor.bg,
            color: statusColor.fg,
            whiteSpace: 'nowrap',
          }}
        >
          {EVENT_STATUS_LABEL[inscription.status]}
        </span>
      </div>

      {selectedPack && (
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(11,93,59,0.08) 0%, rgba(11,93,59,0.02) 100%)',
            border: '1px solid rgba(11,93,59,0.22)',
            borderRadius: 8,
            padding: '8px 12px',
            fontSize: 12,
            color: 'var(--mtc-primary, #0B5D3B)',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            alignSelf: 'flex-start',
          }}
        >
          Pack : {selectedPack.name}
        </div>
      )}
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {inscription.generalIncluded && (
          <li
            style={{
              fontSize: 13,
              color: 'rgba(15,25,22,0.75)',
              padding: '6px 10px',
              background: 'rgba(11,93,59,0.05)',
              borderRadius: 6,
            }}
          >
            ✓ Inscription générale
            {generalPrice > 0 && (
              <span style={{ float: 'right', fontWeight: 600 }}>
                {Math.round(generalPrice).toLocaleString('fr-FR')} MAD
              </span>
            )}
          </li>
        )}
        {selectedAteliers.map((a) => (
          <li
            key={a.id}
            style={{
              fontSize: 13,
              color: 'rgba(15,25,22,0.75)',
              padding: '6px 10px',
              background: 'rgba(15,25,22,0.04)',
              borderRadius: 6,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span>{a.title}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {a.price > 0 && (
                <span style={{ fontWeight: 600 }}>
                  {Math.round(a.price).toLocaleString('fr-FR')} MAD
                </span>
              )}
              {isCancellable && (
                <button
                  type="button"
                  onClick={() => {
                    const newTotal = Math.max(0, inscription.totalAmount - (a.price ?? 0));
                    onRemoveAtelier(a.id, newTotal);
                  }}
                  title="Retirer cet atelier"
                  style={{
                    border: 0,
                    background: 'transparent',
                    color: '#9B1C2C',
                    cursor: 'pointer',
                    fontSize: 16,
                    lineHeight: 1,
                    padding: '2px 6px',
                  }}
                  aria-label="Retirer cet atelier"
                >
                  ×
                </button>
              )}
            </div>
          </li>
        ))}
        {selectedMasterclasses.map((m) => (
          <li
            key={m.id}
            style={{
              fontSize: 13,
              color: 'rgba(15,25,22,0.75)',
              padding: '6px 10px',
              background: 'rgba(212,160,23,0.06)',
              borderLeft: '3px solid #D4A017',
              borderRadius: 6,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#9E7508', letterSpacing: '0.1em', marginRight: 6 }}>
                MASTERCLASS
              </span>
              {m.title}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {m.price > 0 && (
                <span style={{ fontWeight: 600 }}>
                  {Math.round(m.price).toLocaleString('fr-FR')} MAD
                </span>
              )}
              {isCancellable && (
                <button
                  type="button"
                  onClick={() => {
                    const newTotal = Math.max(0, inscription.totalAmount - (m.price ?? 0));
                    onRemoveMasterclass(m.id, newTotal);
                  }}
                  title="Retirer cette masterclass"
                  style={{
                    border: 0,
                    background: 'transparent',
                    color: '#9B1C2C',
                    cursor: 'pointer',
                    fontSize: 16,
                    lineHeight: 1,
                    padding: '2px 6px',
                  }}
                  aria-label="Retirer cette masterclass"
                >
                  ×
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      <div
        style={{
          paddingTop: 8,
          borderTop: '1px solid rgba(15,25,22,0.08)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontSize: 12, color: 'rgba(15,25,22,0.65)' }}>
          <strong style={{ color: '#0F1916' }}>
            Total : {Math.round(inscription.totalAmount).toLocaleString('fr-FR')} MAD
          </strong>{' '}
          · {EVENT_PAYMENT_PREF_LABEL[inscription.paymentPreference] ?? inscription.paymentPreference}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {evenement && (
            <a
              href={`/evenements/${evenement.id}`}
              style={{ fontSize: 12, color: 'var(--mtc-primary, #0B5D3B)' }}
            >
              Voir l'événement →
            </a>
          )}
          {isCancellable && (
            <button
              type="button"
              onClick={onCancelInscription}
              style={{
                fontSize: 12,
                color: '#9B1C2C',
                background: 'transparent',
                border: 0,
                cursor: 'pointer',
              }}
            >
              Annuler l'inscription
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// MesInformations — profile snapshot the member can update directly.
//
// Reads from `members/{uid}.profile`, writes the same shape back. Email is
// shown read-only because it's the key linkMemberInscriptions uses to
// attach past inscriptions; changing it without re-verification would
// silently break that linkage.
// ─────────────────────────────────────────────────────────────────────────

const EDUCATION_OPTIONS: { value: EducationStatus; label: string }[] = [
  { value: 'dentiste', label: 'Dentiste' },
  { value: 'jeune-diplome', label: 'Jeune diplômé·e' },
  { value: 'etudiant-these', label: 'Étudiant·e en instance de thèse' },
];

const EDUCATION_LABEL: Record<EducationStatus, string> = {
  dentiste: 'Dentiste',
  'jeune-diplome': 'Jeune diplômé·e',
  'etudiant-these': 'Étudiant·e en instance de thèse',
  autre: 'Autre',
};

type ProfileFormState = {
  firstName: string;
  lastName: string;
  phone: string;
  birthdate: string;
  nationality: string;
  cabinet: string;
  cityOfPractice: string;
  educationStatus: EducationStatus | '';
  yearsOfPractice: string;
  graduationYear: string;
  faculty: string;
  otherCredentials: string;
};

function formFromMember(m: Member | null): ProfileFormState {
  const p = m?.profile;
  return {
    firstName: p?.firstName ?? '',
    lastName: p?.lastName ?? '',
    phone: p?.phone ?? '',
    birthdate: p?.birthdate ?? '',
    nationality: p?.nationality ?? '',
    cabinet: p?.cabinet ?? '',
    cityOfPractice: p?.cityOfPractice ?? '',
    educationStatus: p?.educationStatus ?? '',
    yearsOfPractice:
      typeof p?.yearsOfPractice === 'number' ? String(p.yearsOfPractice) : '',
    graduationYear:
      typeof p?.graduationYear === 'number' ? String(p.graduationYear) : '',
    faculty: p?.faculty ?? '',
    otherCredentials: p?.otherCredentials ?? '',
  };
}

function MesInformations({
  user,
  member,
  onSaved,
}: {
  user: { uid: string; email: string | null };
  member: Member | null;
  onSaved: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ProfileFormState>(() => formFromMember(member));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const profile = member?.profile;
  const isEmpty = !profile;

  const startEdit = () => {
    setForm(formFromMember(member));
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setForm(formFromMember(member));
    setError(null);
    setEditing(false);
  };

  const save = async () => {
    setError(null);
    if (!form.firstName.trim() || !form.lastName.trim() || !form.phone.trim()) {
      setError('Prénom, nom et téléphone sont requis.');
      return;
    }
    setSaving(true);
    try {
      const snap: MemberProfileSnapshot = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        ...(form.birthdate ? { birthdate: form.birthdate } : {}),
        ...(form.nationality.trim() ? { nationality: form.nationality.trim() } : {}),
        ...(form.cabinet.trim() ? { cabinet: form.cabinet.trim() } : {}),
        ...(form.cityOfPractice.trim() ? { cityOfPractice: form.cityOfPractice.trim() } : {}),
        ...(form.educationStatus ? { educationStatus: form.educationStatus } : {}),
        ...(form.yearsOfPractice
          ? { yearsOfPractice: Number(form.yearsOfPractice) }
          : {}),
        // Year + faculty don't apply to students; other credentials apply to all.
        ...(form.graduationYear && form.educationStatus !== 'etudiant-these'
          ? { graduationYear: Number(form.graduationYear) }
          : {}),
        ...(form.faculty.trim() && form.educationStatus !== 'etudiant-these'
          ? { faculty: form.faculty.trim() }
          : {}),
        ...(form.otherCredentials.trim()
          ? { otherCredentials: form.otherCredentials.trim() }
          : {}),
      };
      await updateMemberProfile(user.uid, snap);
      await onSaved();
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section style={{ marginBottom: 40 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: 16,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <h2
          style={{
            fontFamily: 'var(--mtc-font-mono)',
            fontSize: 11,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'rgba(15,25,22,0.55)',
            margin: 0,
          }}
        >
          Mes informations
        </h2>
        {!editing && (
          <button
            type="button"
            className="mtc-btn mtc-btn-ghost"
            style={{ fontSize: 13, padding: '6px 14px' }}
            onClick={startEdit}
          >
            {isEmpty ? 'Compléter mon profil' : 'Modifier'}
          </button>
        )}
      </div>

      <div
        style={{
          background: 'white',
          border: '1px solid rgba(15,25,22,0.1)',
          borderRadius: 12,
          padding: '20px 22px',
        }}
      >
        {!editing ? (
          isEmpty ? (
            <p style={{ margin: 0, fontSize: 14, color: 'rgba(15,25,22,0.7)' }}>
              Ajoutez vos informations pour pré-remplir automatiquement les
              prochaines inscriptions.
            </p>
          ) : (
            <ProfileGrid>
              <ProfileRow label="Prénom" value={profile.firstName} />
              <ProfileRow label="Nom" value={profile.lastName} />
              <ProfileRow label="Email" value={user.email ?? ''} muted />
              <ProfileRow label="Téléphone" value={profile.phone} />
              <ProfileRow label="Date de naissance" value={profile.birthdate} />
              <ProfileRow label="Nationalité" value={profile.nationality} />
              <ProfileRow
                label="Statut"
                value={
                  profile.educationStatus
                    ? EDUCATION_LABEL[profile.educationStatus]
                    : undefined
                }
              />
              <ProfileRow
                label="Années d'exercice"
                value={
                  typeof profile.yearsOfPractice === 'number'
                    ? String(profile.yearsOfPractice)
                    : undefined
                }
              />
              <ProfileRow label="Cabinet" value={profile.cabinet} />
              <ProfileRow label="Ville d'exercice" value={profile.cityOfPractice} />
              <ProfileRow
                label="Année d'obtention du doctorat"
                value={
                  typeof profile.graduationYear === 'number'
                    ? String(profile.graduationYear)
                    : undefined
                }
              />
              <ProfileRow label="Faculté d'obtention" value={profile.faculty} />
              <ProfileRow
                label="Autres diplômes / certificats"
                value={profile.otherCredentials}
              />
            </ProfileGrid>
          )
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 14,
              }}
            >
              <ProfileInput
                label="Prénom"
                value={form.firstName}
                onChange={(v) => setForm({ ...form, firstName: v })}
              />
              <ProfileInput
                label="Nom"
                value={form.lastName}
                onChange={(v) => setForm({ ...form, lastName: v })}
              />
              <ProfileInput
                label="Email (non modifiable)"
                value={user.email ?? ''}
                disabled
              />
              <ProfileInput
                label="Téléphone"
                value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })}
              />
              <ProfileInput
                label="Date de naissance"
                type="date"
                value={form.birthdate}
                onChange={(v) => setForm({ ...form, birthdate: v })}
              />
              <ProfileInput
                label="Nationalité"
                value={form.nationality}
                onChange={(v) => setForm({ ...form, nationality: v })}
              />
              <ProfileSelect
                label="Statut"
                value={form.educationStatus}
                onChange={(v) =>
                  setForm({ ...form, educationStatus: v as EducationStatus | '' })
                }
                options={[
                  { value: '', label: '— Non renseigné —' },
                  ...EDUCATION_OPTIONS,
                ]}
              />
              <ProfileInput
                label="Années d'exercice"
                type="number"
                value={form.yearsOfPractice}
                onChange={(v) => setForm({ ...form, yearsOfPractice: v })}
              />
              <ProfileInput
                label="Cabinet"
                value={form.cabinet}
                onChange={(v) => setForm({ ...form, cabinet: v })}
              />
              <ProfileInput
                label="Ville d'exercice"
                value={form.cityOfPractice}
                onChange={(v) => setForm({ ...form, cityOfPractice: v })}
              />
              {form.educationStatus !== 'etudiant-these' && (
                <>
                  <ProfileInput
                    label="Année d'obtention du doctorat"
                    type="number"
                    value={form.graduationYear}
                    onChange={(v) => setForm({ ...form, graduationYear: v })}
                  />
                  <ProfileInput
                    label="Faculté d'obtention"
                    value={form.faculty}
                    onChange={(v) => setForm({ ...form, faculty: v })}
                  />
                </>
              )}
              <ProfileInput
                label="Autres diplômes / certificats"
                value={form.otherCredentials}
                onChange={(v) => setForm({ ...form, otherCredentials: v })}
              />
            </div>
            {error && (
              <p style={{ marginTop: 12, color: '#9B1C2C', fontSize: 13 }}>{error}</p>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button
                type="button"
                className="mtc-btn mtc-btn-primary"
                onClick={save}
                disabled={saving}
              >
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
              <button
                type="button"
                className="mtc-btn mtc-btn-ghost"
                onClick={cancel}
                disabled={saving}
              >
                Annuler
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function ProfileGrid({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '14px 24px',
      }}
    >
      {children}
    </div>
  );
}

function ProfileRow({
  label,
  value,
  muted,
}: {
  label: string;
  value: string | undefined;
  muted?: boolean;
}) {
  return (
    <div>
      <div
        style={{
          fontFamily: 'var(--mtc-font-mono)',
          fontSize: 10,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'rgba(15,25,22,0.5)',
          marginBottom: 3,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 14,
          color: muted ? 'rgba(15,25,22,0.55)' : 'var(--mtc-ink)',
          lineHeight: 1.3,
        }}
      >
        {value || <span style={{ color: 'rgba(15,25,22,0.35)' }}>—</span>}
      </div>
    </div>
  );
}

function ProfileInput({
  label,
  value,
  onChange,
  type = 'text',
  disabled,
}: {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  type?: string;
  disabled?: boolean;
}) {
  return (
    <label style={{ display: 'block', fontSize: 12 }}>
      <span
        style={{
          display: 'block',
          fontFamily: 'var(--mtc-font-mono)',
          fontSize: 10,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'rgba(15,25,22,0.55)',
          marginBottom: 4,
        }}
      >
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: 14,
          border: '1px solid rgba(15,25,22,0.18)',
          borderRadius: 8,
          background: disabled ? 'rgba(15,25,22,0.04)' : 'white',
          color: disabled ? 'rgba(15,25,22,0.55)' : 'var(--mtc-ink)',
          fontFamily: 'inherit',
        }}
      />
    </label>
  );
}

function ProfileSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label style={{ display: 'block', fontSize: 12 }}>
      <span
        style={{
          display: 'block',
          fontFamily: 'var(--mtc-font-mono)',
          fontSize: 10,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'rgba(15,25,22,0.55)',
          marginBottom: 4,
        }}
      >
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: 14,
          border: '1px solid rgba(15,25,22,0.18)',
          borderRadius: 8,
          background: 'white',
          color: 'var(--mtc-ink)',
          fontFamily: 'inherit',
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function VerifyBanner({
  onResend,
  onRefresh,
}: {
  onResend: () => Promise<void>;
  onRefresh: () => Promise<void>;
}) {
  return (
    <div
      style={{
        marginBottom: 24,
        padding: '16px 20px',
        background: 'rgba(217,119,6,0.1)',
        border: '1px solid rgba(217,119,6,0.25)',
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <strong style={{ color: '#A36408', fontSize: 14 }}>
        Vérifiez votre adresse email
      </strong>
      <p style={{ fontSize: 13, color: 'rgba(15,25,22,0.7)', margin: 0, lineHeight: 1.5 }}>
        Un email de vérification vous a été envoyé. Cliquez sur le lien qu'il contient pour
        accéder à vos inscriptions. Vos données restent privées tant que l'email n'est pas
        confirmé.
      </p>
      <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
        <button
          type="button"
          className="mtc-btn mtc-btn-ghost"
          style={{ fontSize: 13 }}
          onClick={() => onResend()}
        >
          Renvoyer l'email
        </button>
        <button
          type="button"
          className="mtc-btn mtc-btn-ghost"
          style={{ fontSize: 13 }}
          onClick={() => onRefresh()}
        >
          J'ai cliqué — actualiser
        </button>
      </div>
    </div>
  );
}

function EmptyState({ onBrowse, verified }: { onBrowse: () => void; verified: boolean }) {
  return (
    <div
      style={{
        padding: '40px 24px',
        border: '1px dashed rgba(15,25,22,0.2)',
        borderRadius: 12,
        textAlign: 'center',
        color: 'rgba(15,25,22,0.65)',
      }}
    >
      <p style={{ fontSize: 14, margin: '0 0 12px' }}>
        {verified
          ? "Vous n'avez pas encore d'inscription. Parcourez le catalogue pour en faire une."
          : "Une fois votre email confirmé, vos inscriptions précédentes apparaîtront ici."}
      </p>
      {verified && (
        <button type="button" className="mtc-btn mtc-btn-primary" onClick={onBrowse}>
          Voir les formations
        </button>
      )}
    </div>
  );
}

const TILE_TONES: Record<'vert' | 'or' | 'rouge', { bg: string; fg: string; sub: string }> = {
  vert: { bg: '#0B5D3B', fg: '#ffffff', sub: 'rgba(255,255,255,0.8)' },
  or: { bg: '#C9A961', fg: '#0F1916', sub: 'rgba(15,25,22,0.7)' },
  rouge: { bg: '#C8102E', fg: '#ffffff', sub: 'rgba(255,255,255,0.82)' },
};

function DiscoverTile({
  eyebrow,
  title,
  desc,
  onClick,
  tone,
}: {
  eyebrow: string;
  title: string;
  desc: string;
  onClick: () => void;
  tone: 'vert' | 'or' | 'rouge';
}) {
  const [hover, setHover] = useState(false);
  const c = TILE_TONES[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        textAlign: 'left',
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.bg}`,
        borderRadius: 14,
        padding: '20px 20px 18px',
        cursor: 'pointer',
        font: 'inherit',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        transform: hover ? 'translateY(-4px)' : 'none',
        boxShadow: hover
          ? '0 18px 40px -16px rgba(15,25,22,0.35)'
          : '0 1px 2px rgba(15,25,22,0.04)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--mtc-font-mono)',
          fontSize: 10,
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          color: c.sub,
        }}
      >
        {eyebrow}
      </span>
      <span
        style={{
          fontFamily: 'var(--mtc-font-display)',
          fontSize: 22,
          fontWeight: 400,
          lineHeight: 1.1,
        }}
      >
        {title}
      </span>
      <span style={{ fontSize: 13, lineHeight: 1.45, color: c.sub }}>{desc}</span>
      <span
        style={{
          marginTop: 6,
          fontSize: 16,
          fontWeight: 600,
          color: c.fg,
          transform: hover ? 'translateX(4px)' : 'none',
          transition: 'transform 0.2s ease',
          display: 'inline-block',
        }}
      >
        →
      </span>
    </button>
  );
}

function InscriptionCard({
  inscription: i,
  formationTitle,
  gallery,
  onOpenLightbox,
}: {
  inscription: Inscription;
  formationTitle: string;
  gallery: GalleryItem[];
  onOpenLightbox: (items: GalleryItem[], index: number) => void;
}) {
  const totalPaid = (i.payments ?? []).reduce((s, p) => s + (p.amount || 0), 0);
  const color = STATUS_COLOR[i.status];

  // Filter the member-visible gallery down to this inscription's session.
  // Strict filter on (formationId, sessionId) — what the council wanted for
  // "photos de votre session".
  const sessionPhotos = useMemo(
    () =>
      gallery.filter(
        (g) => g.formationId === i.formationId && g.sessionId === i.sessionId,
      ),
    [gallery, i.formationId, i.sessionId],
  );

  return (
    <article
      style={{
        background: 'white',
        border: '1px solid rgba(15,25,22,0.1)',
        borderRadius: 12,
        padding: '18px 22px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span
          style={{
            background: color.bg,
            color: color.fg,
            fontSize: 11,
            fontWeight: 600,
            padding: '4px 10px',
            borderRadius: 999,
            letterSpacing: '0.04em',
          }}
        >
          {STATUS_LABEL[i.status]}
        </span>
        <span
          style={{
            fontFamily: 'var(--mtc-font-mono)',
            fontSize: 10,
            color: 'rgba(15,25,22,0.45)',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          réf. {i.id.slice(0, 8).toUpperCase()}
        </span>
      </div>
      <h3
        style={{
          fontFamily: 'var(--mtc-font-display)',
          fontSize: 22,
          fontWeight: 400,
          lineHeight: 1.2,
          margin: 0,
        }}
      >
        {formationTitle}
      </h3>
      <div
        style={{
          display: 'flex',
          gap: 24,
          fontSize: 13,
          color: 'rgba(15,25,22,0.7)',
          flexWrap: 'wrap',
        }}
      >
        <span>
          Soumis le{' '}
          <strong>{new Date(i.createdAt).toLocaleDateString('fr-FR')}</strong>
        </span>
        {i.paymentArrangement && (
          <span>
            Paiement <strong>{prettyArrangement(i.paymentArrangement)}</strong>
          </span>
        )}
        {totalPaid > 0 && (
          <span>
            Réglé <strong>{totalPaid.toLocaleString('fr-FR')} MAD</strong>
          </span>
        )}
        {i.group && (
          <span>
            Groupe <strong>{i.group}</strong>
          </span>
        )}
      </div>

      {/* Certificate row — appears as soon as the admin uploads it. */}
      {i.certificateUrl && (
        <div
          style={{
            marginTop: 10,
            padding: '12px 14px',
            background: 'rgba(11,93,59,0.08)',
            border: '1px solid rgba(11,93,59,0.18)',
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontFamily: 'var(--mtc-font-mono)',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: '#0B5D3B',
                marginBottom: 2,
              }}
            >
              Certificat disponible
            </div>
            <div style={{ fontSize: 13, color: 'rgba(15,25,22,0.75)' }}>
              Téléchargez votre attestation officielle au format PDF.
            </div>
          </div>
          <a
            href={i.certificateUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mtc-btn mtc-btn-primary"
            style={{ textDecoration: 'none' }}
          >
            Télécharger le certificat ↗
          </a>
        </div>
      )}

      {/* Session photos — only the ones tagged to this exact session. */}
      {sessionPhotos.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div
            style={{
              fontSize: 11,
              fontFamily: 'var(--mtc-font-mono)',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: 'rgba(15,25,22,0.55)',
              marginBottom: 8,
            }}
          >
            Photos de votre session · {sessionPhotos.length}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
              gap: 6,
            }}
          >
            {sessionPhotos.slice(0, 8).map((p, idx) => (
              <button
                type="button"
                key={p.id}
                onClick={() => onOpenLightbox(sessionPhotos, idx)}
                style={{
                  all: 'unset',
                  cursor: 'zoom-in',
                  aspectRatio: '4/3',
                  borderRadius: 6,
                  overflow: 'hidden',
                  background: 'rgba(15,25,22,0.05)',
                }}
                aria-label={p.caption || (p.kind === 'video' ? 'Vidéo de session' : 'Photo de session')}
              >
                {p.kind === 'video' ? (
                  <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                    <video
                      src={`${p.url}#t=0.1`}
                      preload="metadata"
                      muted
                      playsInline
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'grid',
                        placeItems: 'center',
                        pointerEvents: 'none',
                      }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 999,
                          background: 'rgba(0,0,0,0.55)',
                          color: 'white',
                          fontSize: 14,
                          display: 'grid',
                          placeItems: 'center',
                        }}
                      >
                        ▶
                      </div>
                    </div>
                  </div>
                ) : (
                  <img
                    src={p.url}
                    alt={p.caption ?? ''}
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                )}
              </button>
            ))}
          </div>
          {sessionPhotos.length > 8 && (
            <button
              type="button"
              className="mtc-btn mtc-btn-ghost"
              style={{ marginTop: 8, fontSize: 13 }}
              onClick={() => onOpenLightbox(sessionPhotos, 0)}
            >
              Voir les {sessionPhotos.length} photos
            </button>
          )}
        </div>
      )}
    </article>
  );
}

function prettyArrangement(p: string): string {
  return (
    {
      full: 'Paiement complet',
      installments: 'Échelonné',
      'installments-2': '2 échéances',
      'installments-3': '3 échéances',
      invite: 'Invité (offert)',
      remise: 'Avec remise',
    }[p] ?? p
  );
}
